import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle, CircleNotch, XCircle } from '@phosphor-icons/react'
import type { PulsePergunta } from '@/components/pulse/pulse-shared'
import { Card, CardContent } from '@/components/ui/card'
import {
  getPublicPulseVerificacao,
  submitPublicPulseResposta,
} from '@/api/pulse-carteira/pulse-carteira.actions'
import type { PulsePublicVerificacaoData } from '@/api/pulse-carteira/pulse-carteira.schema'
import {
  ConfirmacaoStep,
  PesquisaWizard,
  type PulseConfirmacaoData,
} from '@/components/pulse/pulse-shared'
import { getApiErrorMessage } from '@/lib/api-error'

export const Route = createFileRoute('/pulse/$token')({
  component: PulseCarteiraPage,
})

type PulseData = PulsePublicVerificacaoData & {
  perguntas: Array<PulsePergunta>
}

function PulseCarteiraPage() {
  const { token } = Route.useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<PulseData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [step, setStep] = useState<'confirmacao' | 'pesquisa' | 'obrigado'>('confirmacao')
  const [resposta, setResposta] = useState<string | null>(null)
  const [respostasPesquisa, setRespostasPesquisa] = useState<Record<string, any>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadData(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [token])

  async function loadData(isCancelled?: () => boolean) {
    setIsLoading(true)
    setError(null)
    try {
      const json = await getPublicPulseVerificacao(token)
      if (isCancelled?.()) return
      const payload = {
        ...json.data,
        perguntas: (json.data.perguntas || []) as Array<PulsePergunta>,
      }
      setData(payload)
      if (json.data.jaRespondido) setStep('obrigado')
    } catch (err: unknown) {
      if (isCancelled?.()) return
      setError(getApiErrorMessage(err, 'Erro ao carregar dados'))
    } finally {
      if (!isCancelled?.()) setIsLoading(false)
    }
  }

  async function handleConfirmacao(value: string) {
    setResposta(value)
    if (value === 'DISPONIVEL') {
      await submitResposta(value, {})
    } else {
      setStep('pesquisa')
    }
  }

  async function submitResposta(resp: string, pesquisa: Record<string, any>) {
    setIsSubmitting(true)
    try {
      await submitPublicPulseResposta(token, {
        resposta: resp,
        respostaPesquisa: pesquisa,
      })
      setStep('obrigado')
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao enviar resposta'))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleSubmitPesquisa() {
    if (!resposta) return
    await submitResposta(resposta, respostasPesquisa)
  }

  const perguntas = useMemo(() => {
    if (!data?.perguntas || !Array.isArray(data.perguntas)) return []
    return data.perguntas
      .filter((p: PulsePergunta) => p.ativa !== false)
      .filter((p: PulsePergunta) => {
        if (!p.exibirQuando || p.exibirQuando.length === 0) return true
        return p.exibirQuando.includes(
          resposta as 'DISPONIVEL' | 'VENDIDO' | 'LOCADO',
        )
      })
      .sort((a: PulsePergunta, b: PulsePergunta) => (a.ordem ?? 0) - (b.ordem ?? 0))
  }, [data?.perguntas, resposta])

  const perguntasVisiveis = useMemo(() => {
    return perguntas.filter((p: PulsePergunta) => {
      if (!p.condicional) return true
      const valorRef = respostasPesquisa[p.condicional.perguntaId]?.valor
      return valorRef === p.condicional.valor
    })
  }, [perguntas, respostasPesquisa])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Card className="w-full max-w-md mx-4 shadow-lg">
          <CardContent className="flex items-center justify-center py-16">
            <CircleNotch className="h-8 w-8 animate-spin text-primary mr-3" />
            <span className="text-lg text-muted-foreground">Carregando...</span>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-red-50 to-pink-50">
        <Card className="w-full max-w-md mx-4 shadow-lg">
          <CardContent className="py-16 text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold mb-2">Link inválido</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!data) return null

  if (step === 'obrigado') {
    return (
      <ObrigadoStep
        data={data}
        resposta={resposta || data.respostaAnterior}
      />
    )
  }

  if (step === 'pesquisa') {
    return (
      <PesquisaWizard
        perguntasVisiveis={perguntasVisiveis}
        respostasPesquisa={respostasPesquisa}
        setResposta={(perguntaId, valor) =>
          setRespostasPesquisa((prev) => ({ ...prev, [perguntaId]: { valor } }))
        }
        resposta={resposta}
        imovelCodigo={data.imovel.codigo}
        onSubmit={handleSubmitPesquisa}
        isSubmitting={isSubmitting}
        error={error}
      />
    )
  }

  const confirmacaoData: PulseConfirmacaoData = {
    imovel: {
      codigo: data.imovel.codigo,
      titulo: data.imovel.titulo,
      fotos: data.imovel.fotos,
      localizacao: data.imovel.localizacao
        ? {
            rua: data.imovel.localizacao.rua ?? undefined,
            numero: data.imovel.localizacao.numero ?? undefined,
            bairro: data.imovel.localizacao.bairro ?? undefined,
            cidade: data.imovel.localizacao.cidade ?? undefined,
          }
        : undefined,
    },
    contato: { nome: data.contato.nome ?? '' },
    dashboard: {
      titulo: data.dashboard.titulo,
      logo: data.dashboard.logo ?? undefined,
    },
  }

  return (
    <ConfirmacaoStep
      data={confirmacaoData}
      onConfirmacao={handleConfirmacao}
      isSubmitting={isSubmitting}
      error={error}
    />
  )
}

function ObrigadoStep({
  data,
  resposta,
}: {
  data: PulseData
  resposta: string | null | undefined
}) {
  const isDisponivel = resposta === 'DISPONIVEL'

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDisponivel
          ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50'
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}
    >
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="py-12 text-center">
          <CheckCircle
            className={`h-20 w-20 mx-auto mb-4 ${
              isDisponivel ? 'text-green-500' : 'text-blue-500'
            }`}
          />

          <h2 className="text-2xl font-bold mb-2">
            {isDisponivel ? 'Obrigado pela confirmação!' : 'Resposta registrada!'}
          </h2>

          <p className="text-muted-foreground mb-6">
            {isDisponivel
              ? `Ótimo! Seu imóvel ${data.imovel.codigo} continua em nosso portfólio.`
              : `Agradecemos por nos informar sobre o imóvel ${data.imovel.codigo}. Suas respostas são muito importantes para nós.`}
          </p>

          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
            <p className="font-medium">{data.dashboard.titulo}</p>
            <p>Pode fechar esta página.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
